/**
 * Terms and conditions.
 *
 * Every mechanical claim on this page is the behaviour the code actually has:
 * the figures come from the shared constants the payment path uses, and nothing
 * is promised here that the product does not do. Where a term describes a
 * practice rather than a mechanism it is written narrowly, for the same reason.
 */

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import {
  LISTING_LIFETIME_DAYS,
  MINIMUM_FIRST_BID_CENTS,
  PASS_INCREMENT_CENTS,
} from '@bidtohire/shared';

import { Money } from '../components/Money';
import { PolicyPage, type PolicySection } from './policy-shared';

export const TERMS_EFFECTIVE = '21 August 2026';

const SECTIONS: readonly PolicySection[] = [
  {
    id: 'acceptance',
    title: 'Acceptance',
    body: (
      <>
        <p>
          These terms are an agreement between you and the operator of bidtohire.me (“we”, “us”).
          They govern your use of the site and of everything on it. By browsing the site, creating a
          company account, posting a listing, or making a payment, you accept them. If you do not
          accept them, do not use the site.
        </p>
        <p>
          If you accept these terms for a company, you confirm that you are authorised to bind that
          company, and “you” means both you and the company.
        </p>
      </>
    ),
  },
  {
    id: 'service',
    title: 'The service',
    body: (
      <>
        <p>
          bidtohire.me is a job board on which position is bought. Companies publish job listings and
          pay to rank them; the amount a listing has been paid is its rank and is displayed publicly
          beside it. Anyone can browse and search the boards without an account.
        </p>
        <p>
          We are a board and nothing more. We do not take applications, screen candidates, verify the
          companies that post, employ anyone, or take any part in hiring. Every listing links out to
          the employer’s own posting, and an application is made there, under that employer’s terms.
          We are not a party to anything that follows.
        </p>
        <p>
          The service is offered as it is, and we may change, suspend, or discontinue any part of it.
        </p>
      </>
    ),
  },
  {
    id: 'accounts',
    title: 'Company accounts',
    body: (
      <>
        <p>
          An account is needed only to post and manage listings. You register with an email address
          and a password, and you confirm the address with a code we send to it. One account
          represents one company.
        </p>
        <p>
          You are responsible for the accuracy of what you register, for keeping your password
          confidential, and for everything done through your account. Tell us promptly if you believe
          it has been used without your permission. You must be able to form a binding contract and
          must not create an account on behalf of a company you do not represent.
        </p>
        <p>
          You may close your account yourself from the dashboard at any time. Closing it removes your
          listings, your company profile, and your sign-in. See{' '}
          <Link to="/privacy">the privacy policy</Link> for what is kept afterwards.
        </p>
      </>
    ),
  },
  {
    id: 'listings',
    title: 'Listings and ranking',
    body: (
      <>
        <p>
          Posting is paid first. A payment of at least{' '}
          <Money cents={MINIMUM_FIRST_BID_CENTS} /> reserves a listing and buys its opening
          position; the listing is published when you submit its details, which you may do at any
          time afterwards. Until it is published it appears nowhere on the site and has no public
          address.
        </p>
        <p>
          Rank is the cumulative total a listing has been paid, and nothing else affects it. You may
          bid any whole-dollar amount at or above the minimums stated above, and your listing takes
          whatever position that total earns it; no position carries a price of its own. To take a
          position you must reach a total above the total of the listing holding it, which in whole
          dollars means that listing’s total plus{' '}
          <Money cents={PASS_INCREMENT_CENTS} />. Raising is a top-up: you pay the
          difference between the total you hold and the total you want, and a competitor has to beat
          the total rather than the difference. Equal totals stay in the order they were reached, so
          matching a total does not take the position. One payment counts on all three boards a
          listing appears on — the global board, its category, and its specialism. The total paid is
          shown publicly on the listing, and you agree to its display.
        </p>
        <p>
          Some listings shown at the minimum listing amount were published by bidtohire.me from job
          postings that were publicly available elsewhere, as launch content, rather than purchased
          by the employer named on them. Those listings link to the employer’s own posting. An
          employer may claim a listing that names it, or ask for the listing to be removed, using the
          contact route in section 15, and we will act on such a request.
        </p>
        <p>
          A published listing runs {LISTING_LIFETIME_DAYS} days from publication or from its last
          renewal. Renewal is free and is done from the dashboard; the total paid and the position
          carry over. A listing that is not renewed leaves every board and stops being ranked; its own page
          stays reachable, marked expired, until it is renewed or removed.
        </p>
        <p>
          You keep ownership of what you post, and you grant us a non-exclusive, worldwide,
          royalty-free licence to host, display, reproduce and distribute it for the purpose of
          operating and promoting the site. That licence lasts as long as the listing is on the site.
        </p>
      </>
    ),
  },
  {
    id: 'payments',
    title: 'Payments and refunds',
    body: (
      <>
        <p>
          Payments are one-time card payments in US dollars, in whole dollars, taken through Stripe.
          Stripe collects and processes the card details; we never see or store a card number.
          Stripe’s own terms apply to that part of the transaction. Prices are shown before you pay
          and the amount you are charged is the amount you confirm.
        </p>
        <p>
          <strong>Payments are non-refundable.</strong> Rank is claimed when the payment completes,
          not when a listing is created, and a paid position is not returned, transferred, or
          credited if you later delete the listing, close your account, let the listing expire, or are
          outbid. Being outbid is the ordinary operation of the board and is not a fault.
        </p>
        <p>
          A payment may be made before a listing has any details, which is how the posting flow
          works: it reserves the listing. That payment is taken and is non-refundable at that
          point; the listing publishes as soon as you submit the posting details, and the
          reservation does not expire. If a payment completes and the position or publication it
          bought does not follow, contact us and we will correct it or refund that payment. If we remove a listing under section 07
          for a breach of these terms, no refund is due.
        </p>
        <p>
          You are responsible for any taxes on your use of the service other than taxes on our
          income. Do not attempt a chargeback in place of contacting us; we may suspend an account
          with an unresolved chargeback.
        </p>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    title: 'Acceptable use',
    body: (
      <>
        <p>A listing you post must be a genuine, currently open role. You must not:</p>
        <ul>
          <li>
            post a role that does not exist, is already filled, or is advertised on terms other than
            the ones stated, including a salary range you do not intend to honour;
          </li>
          <li>
            post on behalf of a company without its permission, or misrepresent who is hiring;
          </li>
          <li>
            post anything unlawful, or anything that discriminates in a way employment law forbids;
          </li>
          <li>
            use a listing as advertising for something that is not a job, or point the apply link
            anywhere other than the employer’s own posting for that role;
          </li>
          <li>
            ask candidates for payment, financial details, or documents a lawful hiring process would
            not ask for at that stage;
          </li>
          <li>
            post content you do not have the rights to, or that infringes anyone’s rights;
          </li>
          <li>
            scrape, crawl, or bulk-copy the site or its data by any automated means, or republish the
            boards or their contents as a dataset or a competing listing service;
          </li>
          <li>
            interfere with the site or its infrastructure, probe or bypass its access controls, use it
            to send unsolicited mail, or submit a payment you are not authorised to make;
          </li>
          <li>
            create accounts or payments designed to manipulate the boards, misstate what a listing has
            been paid, or otherwise game rank other than by paying for it.
          </li>
        </ul>
        <p>
          You must not use the feedback form to send anything unlawful, abusive, or misleading, and
          the contact details of anyone named on the site must not be collected for any purpose other
          than applying for the role.
        </p>
      </>
    ),
  },
  {
    id: 'removal',
    title: 'Removal and suspension',
    body: (
      <>
        <p>
          We may remove or refuse any listing, and suspend or close any account, at our discretion —
          with or without notice — including where we believe these terms have been breached, where a
          listing is inaccurate or unlawful, where a payment is disputed or reversed, or where the use
          of the site threatens its operation or its other users.
        </p>
        <p>
          We do not review listings before they are published and we do not undertake to monitor them.
          Removing one thing does not oblige us to remove another. Where a listing is removed for a
          breach, section 05 governs whether anything is refunded.
        </p>
      </>
    ),
  },
  {
    id: 'ip',
    title: 'Intellectual property',
    body: (
      <>
        <p>
          The site — its software, design, text, arrangement, and the bidtohire.me name and mark —
          belongs to us or to our licensors and is protected by intellectual property law. These terms
          give you no right in it beyond using the site as it is intended to be used.
        </p>
        <p>
          Content posted by a company remains that company’s, subject to the licence in section 04.
          Company names and logos on the site belong to the companies they identify and are shown to
          identify them. If you believe something on the site infringes your rights, contact us with
          enough detail to find it and we will look into it.
        </p>
      </>
    ),
  },
  {
    id: 'disclaimers',
    title: 'Disclaimers',
    body: (
      <>
        <p>
          The site is provided “as is” and “as available”, without warranty of any kind. To the
          fullest extent the law allows, we disclaim all warranties, express or implied, including
          merchantability, fitness for a particular purpose, title, and non-infringement, and we do
          not warrant that the site will be uninterrupted, secure, or error-free.
        </p>
        <p>
          We do not verify listings, companies, salary ranges, or the destinations that listings link
          to, and we do not endorse them. We do not promise that a listing will be seen, clicked,
          applied to, or that it will fill a role, and view and click counts are our own best-effort
          measurements rather than audited figures. Nothing here is legal, financial, or employment
          advice.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of liability',
    body: (
      <>
        <p>
          To the fullest extent the law allows, we are not liable for any indirect, incidental,
          special, consequential, exemplary, or punitive damages, or for lost profits, lost revenue,
          lost data, lost goodwill, or lost hiring or employment opportunities, arising out of or
          relating to the site — whether the claim is in contract, tort, or otherwise, and even if we
          were told such damages were possible.
        </p>
        <p>
          Our total liability for all claims relating to the site is limited to the greater of the
          amount you paid us in the twelve months before the event giving rise to the claim, or one
          hundred US dollars.
        </p>
        <p>
          Some jurisdictions do not allow certain exclusions or limitations, so parts of this section
          may not apply to you; where that is so, our liability is limited as far as the law allows.
        </p>
      </>
    ),
  },
  {
    id: 'indemnity',
    title: 'Indemnification',
    body: (
      <p>
        You will indemnify and hold harmless the operator of bidtohire.me, and anyone acting on its
        behalf, from any claim, demand, loss, liability, or expense — including reasonable legal fees
        — arising out of your use of the site, anything you post or pay for, your breach of these
        terms or of any law, or any dispute between you and a candidate, an employer, or a third
        party. We may take over the defence of any such claim at your expense, and you will not settle
        it in a way that binds us without our written consent.
      </p>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to these terms',
    body: (
      <p>
        We may change these terms. The version on this page is the one in force, and the date under
        the title is when it took effect. A material change will be reflected in that date, and
        continuing to use the site after it takes effect is acceptance of the changed terms. If you do
        not accept a change, stop using the site and close your account. Changes do not apply
        retroactively to a payment already made.
      </p>
    ),
  },
  {
    id: 'termination',
    title: 'Termination',
    body: (
      <p>
        You may stop using the site at any time and close your account from the dashboard. We may
        suspend or end your access as described in section 07. On termination, your right to use the
        site ends and your listings stop being shown; sections 05, 08, 09, 10, 11 and 14, and any
        other term that by its nature should survive, survive it.
      </p>
    ),
  },
  {
    id: 'law',
    title: 'Governing law',
    body: (
      <p>
        These terms are governed by the laws of the United States and of the state in which the
        operator of bidtohire.me resides, without regard to conflict-of-laws rules, and the courts of
        that state have exclusive jurisdiction over any dispute arising from them. The site is
        operated from the United States; if you use it from elsewhere, you do so on your own
        initiative and are responsible for complying with your local law. The United Nations
        Convention on Contracts for the International Sale of Goods does not apply.
      </p>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    body: (
      <p>
        Use the <strong>Report a bug</strong> link in the footer of any page. It opens a form that
        reaches the operator directly and needs no account; leave an email address in it if you want
        an answer. That form is the contact route for everything in these terms — questions, listing
        claims, removal requests, refund corrections, and infringement notices.
      </p>
    ),
  },
];

export function TermsPage(): ReactNode {
  return (
    <PolicyPage
      eyebrow="Legal"
      title="Terms and conditions"
      lede={
        <>
          The rules for using bidtohire.me: what the board is, how a paid position works, and what
          each of us is responsible for.
        </>
      }
      effective={`In effect from ${TERMS_EFFECTIVE}.`}
      sections={SECTIONS}
    />
  );
}
